import { useEffect, useState } from "react";
import { Marker, Popup, Polyline, CircleMarker } from "react-leaflet";
import L from "leaflet";
import { api } from "@/lib/api";
import { todayISO } from "@/lib/format";
import { SectionLabel } from "@/components/Brand";
import { SecureGate, SecureLockButton, useSecure } from "@/components/SecureGate";

const TECH = "#0f1710";
const ROUTE = "#2563eb";
const initials = (n) => n.split(" ").map((x) => x[0]).join("").slice(0, 2).toUpperCase();
const hhmm = (iso) => new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
const ago = (iso) => { const m = Math.round((Date.now() - new Date(iso)) / 60000); return m < 1 ? "ora" : m < 60 ? `${m} min fa` : `${Math.floor(m / 60)} h fa`; };
const SOURCE = { job_start: "Avvio intervento", job_close: "Chiusura intervento", auto: "Rilevamento", entrata: "Entrata (timbratura)", uscita: "Uscita (timbratura)" };
const DOT = { auto: ROUTE, job_start: "#61a229", job_close: "#ef4444", entrata: "#0f1710", uscita: "#0f1710" };

const techPin = (label) => L.divIcon({
  className: "", iconSize: [34, 34], iconAnchor: [17, 17],
  html: `<div style="width:34px;height:34px;border-radius:10px;background:${TECH};color:#a2ca6b;display:grid;place-items:center;font:700 12px 'JetBrains Mono',monospace;border:2px solid #a2ca6b;box-shadow:0 2px 8px rgba(0,0,0,.4)">${label}</div>`,
});

export function useTechTracking(date) {
  const [live, setLive] = useState([]);
  const [techs, setTechs] = useState([]);
  const [techId, setTechId] = useState("");
  const [route, setRoute] = useState([]);
  const unlocked = useSecure();
  useEffect(() => {
    if (!unlocked) { setLive([]); setTechs([]); return undefined; }
    const load = () => {
      if (date === todayISO()) api.get("/admin/tracking/live").then((r) => setLive(r.data)).catch(() => {});
      else setLive([]);
      api.get("/admin/tracking/technicians", { params: { date } }).then((r) => setTechs(r.data)).catch(() => {});
    };
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [date, unlocked]);
  useEffect(() => { setTechId(""); }, [date, unlocked]);
  useEffect(() => {
    if (!techId || !unlocked) { setRoute([]); return; }
    api.get("/admin/tracking/route", { params: { technician_id: techId, date } }).then((r) => setRoute(r.data));
  }, [techId, date, unlocked]);
  return { live, techs, techId, setTechId, route };
}

export function TechTrackingLayer({ live, route }) {
  return (
    <>
      {route.length > 1 && <Polyline positions={route.map((p) => [p.lat, p.lng])} pathOptions={{ color: ROUTE, weight: 3, opacity: 0.8, dashArray: "6 6" }} />}
      {route.map((p, i) => (
        <CircleMarker key={i} center={[p.lat, p.lng]} radius={p.source === "auto" ? 4 : 7}
          pathOptions={{ color: "#fff", weight: 2, fillColor: DOT[p.source] || ROUTE, fillOpacity: 1 }}>
          <Popup><span className="text-xs">{SOURCE[p.source]} · {hhmm(p.at)}</span></Popup>
        </CircleMarker>
      ))}
      {live.map((t) => (
        <Marker key={t.technician_id} position={[t.lat, t.lng]} icon={techPin(initials(t.name))} zIndexOffset={1000}>
          <Popup><div className="text-xs" data-testid={`tech-live-popup-${t.technician_id}`}><p className="font-semibold">{t.name}</p><p>Aggiornato {ago(t.at)} ({hhmm(t.at)})</p></div></Popup>
        </Marker>
      ))}
    </>
  );
}

export function TechTrackingPanel({ live, techs, techId, setTechId, route }) {
  return (
    <div className="rounded-2xl border bg-white p-4" data-testid="tech-tracking-panel">
      <div className="flex items-center justify-between"><SectionLabel>Posizione tecnici</SectionLabel><SecureLockButton /></div>
      <div className="mt-3"><SecureGate>
      <div className="space-y-1.5 text-sm" data-testid="tech-live-list">
        {live.length ? live.map((t) => (
          <p key={t.technician_id} className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2"><span className="h-3 w-3 rounded-[3px]" style={{ background: TECH }} />{t.name}</span>
            <span className="font-mono text-xs text-slate-500">{ago(t.at)}</span>
          </p>
        )) : <p className="text-xs text-slate-500" data-testid="tech-live-empty">Nessuna posizione recente.</p>}
      </div>
      <label className="mt-4 block text-xs font-semibold text-slate-600">Percorso del giorno</label>
      <select value={techId} onChange={(e) => setTechId(e.target.value)} data-testid="tech-route-select"
        className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm">
        <option value="">{techs.length ? "Scegli un tecnico" : "Nessun percorso registrato"}</option>
        {techs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
      </select>
      {techId && <p className="mt-2 text-xs text-slate-500" data-testid="tech-route-info">{route.length} rilevamenti{route.length ? ` · ${hhmm(route[0].at)}–${hhmm(route[route.length - 1].at)}` : ""}</p>}
      </SecureGate></div>
    </div>
  );
}
